import { Http, baseApi, RtkqRequest, rtkq, asky, ListSegment } from '@app/base';
import { FilterRule, PagingRule, SortRule } from '@webinex/asky';
import {
  GetCatalogCareHomesLookupListDocument,
  GetCatalogCareHomesDocument,
  GetCatalogCareHomesListSegmentDocument,
} from './graphql';
import {
  AddCatalogCareHomeRequest,
  ArchiveCatalogCareHomeRequest,
  CatalogCareHome,
  CatalogCareHomeDetail,
  CatalogCareHomeLookup,
  DeleteCatalogCareHomeRequest,
  UpdateCatalogCareHomeRequest,
} from './catalogCareHomeTypes';

const getSearchFilter = (searchValue?: string | null) => {
  return searchValue
    ? ({
        operator: 'or',
        children: [
          {
            fieldId: 'name',
            operator: 'contains',
            value: searchValue,
          },
          {
            fieldId: 'address.city',
            operator: 'contains',
            value: searchValue,
          },
          {
            fieldId: 'address.street',
            operator: 'contains',
            value: searchValue,
          },
          {
            fieldId: 'address.region',
            operator: 'contains',
            value: searchValue,
          },
          {
            fieldId: 'address.zipCode',
            operator: 'contains',
            value: searchValue,
          },
        ],
      } satisfies FilterRule)
    : undefined;
};

export const catalogCareHomesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCatalogCareHome: builder.query<CatalogCareHomeDetail, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.axios((args) => Http.sw().get<CatalogCareHomeDetail>(`/api/catalog-care-home/${args.id}`)),
      providesTags: ['medical-record:catalog-care-home'],
    }),

    getCatalogCareHomes: builder.query<
      CatalogCareHome[],
      RtkqRequest<{ includeProviderId?: string; ids?: string[]; archived?: boolean | null }>
    >({
      queryFn: rtkq.graphql(GetCatalogCareHomesDocument, {
        select: (x) => x.catalogCareHomes,
        vars: ({ includeProviderId, ids, archived = false }) => {
          const providerFilterRule: FilterRule = includeProviderId
            ? { fieldId: 'providerId', operator: 'in', values: [null, includeProviderId] }
            : { fieldId: 'providerId', operator: '=', value: null };

          const idsFilterRule = ids && { fieldId: 'id', operator: 'in', values: ids };

          const filterRule: FilterRule = {
            operator: 'and',
            children: [
              idsFilterRule ? (idsFilterRule as FilterRule) : providerFilterRule,
              { fieldId: 'archived', operator: '=', value: archived },
            ],
          };

          return { query: JSON.stringify({ filterRule }) };
        },
      }),
      providesTags: ['medical-record:catalog-care-home'],
    }),

    getCatalogCareHomesListSegment: builder.query<
      ListSegment<CatalogCareHome>,
      RtkqRequest<{
        searchValue?: string | null;
        filterRule?: FilterRule | null;
        sortRule?: SortRule | null;
        pagingRule?: PagingRule | null;
      }>
    >({
      queryFn: rtkq.graphql(GetCatalogCareHomesListSegmentDocument, {
        select: (x) => x.careHomesListSegment,
        vars: ({ searchValue, ...query }) => {
          const searchFilter = getSearchFilter(searchValue);
          const filterRule = asky.filter.create([searchFilter || false, query.filterRule || false]);
          const queryWithSearch = {
            ...query,
            filterRule,
          };
          return {
            query: queryWithSearch,
          };
        },
      }),
      providesTags: ['medical-record:catalog-care-home'],
    }),

    getCatalogCareHomeLookupList: builder.query<CatalogCareHomeLookup[], RtkqRequest<{ archived?: boolean }>>(
      {
        queryFn: rtkq.graphql(GetCatalogCareHomesLookupListDocument, {
          vars: ({ archived }) => {
            const archivedRule = asky.filter.create([
              archived != null && { fieldId: 'archived', operator: '=', value: archived },
            ]);
            const filterRules = archived != null ? [archivedRule!] : undefined;
            return { query: JSON.stringify(asky.query({ filterRules })) };
          },
          select: (x) => x.catalogCareHomes!,
        }),
        providesTags: ['medical-record:catalog-care-home'],
      },
    ),

    addCatalogCareHome: builder.mutation<string, RtkqRequest<AddCatalogCareHomeRequest>>({
      queryFn: rtkq.axios((args) => Http.post<string>(`/api/catalog-care-home`, args)),
      invalidatesTags: () => ['medical-record:catalog-care-home'],
    }),

    updateCatalogCareHome: builder.mutation<void, RtkqRequest<UpdateCatalogCareHomeRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/catalog-care-home/${args.id}`, args)),
      invalidatesTags: ['medical-record:catalog-care-home'],
    }),

    deleteCatalogCareHome: builder.mutation<void, RtkqRequest<DeleteCatalogCareHomeRequest>>({
      queryFn: rtkq.axios(({ id }) => Http.delete<void>(`/api/catalog-care-home/${id}`)),
      invalidatesTags: ['medical-record:catalog-care-home'],
    }),

    archiveCatalogCareHome: builder.mutation<void, RtkqRequest<ArchiveCatalogCareHomeRequest>>({
      queryFn: rtkq.axios(({ id }) => Http.put<void>(`/api/catalog-care-home/${id}/archive`)),
      invalidatesTags: ['medical-record:catalog-care-home'],
    }),
  }),
});

export const {
  useGetCatalogCareHomesListSegmentQuery,
  useLazyGetCatalogCareHomesListSegmentQuery,
  useGetCatalogCareHomeQuery,
  useLazyGetCatalogCareHomesQuery,
  useAddCatalogCareHomeMutation,
  useUpdateCatalogCareHomeMutation,
  useArchiveCatalogCareHomeMutation,
  useDeleteCatalogCareHomeMutation,
  useLazyGetCatalogCareHomeLookupListQuery,
} = catalogCareHomesApi;
